/**
 * Admin-panel translation dictionary (EN + HI).
 *
 * Organized by domain namespace:
 *   sidebar.*      — left-nav menu labels
 *   action.*       — common buttons (save, edit, delete, etc.)
 *   status.*       — state labels reused across features (order, payment, user)
 *   dashboard.*    — home dashboard cards and charts
 *   common.*       — table headers, filters, form labels used everywhere
 *   page.*         — per-page titles + subtitles
 *   stats.*        — per-feature stats-card labels
 *   order.*        — orders module
 *   product.*      — products module
 *   customer.*     — customers module
 *   blog.*         — blog module
 *   vip.*          — VIP wallet module
 *   reports.*      — reports module
 *   whatsapp.*     — WhatsApp templates module
 *   auth.*         — login screens
 *   language.*     — language toggle
 *
 * Adding translations: drop the key + English + Hindi here. No code change
 * needed in the component that uses `t(key)`.
 */
export const resources = {
  en: {
    translation: {
      // ── Sidebar ──
      "sidebar.dashboard": "Dashboard",
      "sidebar.products": "Products",
      "sidebar.orders": "Orders",
      "sidebar.customers": "Customers",
      "sidebar.categories": "Categories",
      "sidebar.brands": "Brands",
      "sidebar.flashSales": "Flash Sales",
      "sidebar.promoCodes": "Promo Codes",
      "sidebar.featuredSections": "Featured Sections",
      "sidebar.homeBanners": "Home Banners",
      "sidebar.blog": "Blog",
      "sidebar.newsletter": "Newsletter",
      "sidebar.notifications": "Notifications",
      "sidebar.supportTickets": "Support Tickets",
      "sidebar.returnRequests": "Return Requests",
      "sidebar.marketing": "Marketing",
      "sidebar.warehouse": "Warehouse",
      "sidebar.deliveryBoys": "Delivery Boys",
      "sidebar.analytics": "Analytics",
      "sidebar.payments": "Payments",
      "sidebar.reports": "Reports",
      "sidebar.vipWallets": "VIP Wallets",
      "sidebar.whatsappTemplates": "WhatsApp Templates",
      "sidebar.appSettings": "App Settings",
      "sidebar.auditLogs": "Change Log",
      "sidebar.mediaManager": "Media Library",
      "sidebar.pendingOrders": "Pending Orders",
      "sidebar.codCash": "COD Cash",
      "sidebar.customerComms": "Customer Comms",
      "sidebar.adminUsers": "Admin Users",
      "sidebar.settings": "Settings",
      "sidebar.content": "Content",
      "sidebar.support": "Support",
      "sidebar.helpAndDocs": "Help & Docs",
      "sidebar.legalPages": "Legal Pages",

      // ── Common actions ──
      "action.save": "Save",
      "action.saveChanges": "Save Changes",
      "action.cancel": "Cancel",
      "action.delete": "Delete",
      "action.edit": "Edit",
      "action.add": "Add",
      "action.create": "Create",
      "action.update": "Update",
      "action.back": "Back",
      "action.download": "Download",
      "action.upload": "Upload",
      "action.search": "Search",
      "action.filter": "Filter",
      "action.refresh": "Refresh",
      "action.apply": "Apply",
      "action.confirm": "Confirm",
      "action.close": "Close",
      "action.reset": "Reset",
      "action.submit": "Submit",
      "action.export": "Export",
      "action.import": "Import",
      "action.view": "View",
      "action.viewAll": "View All",
      "action.viewDetails": "View Details",
      "action.active": "Active",
      "action.inactive": "Inactive",
      "action.activate": "Activate",
      "action.deactivate": "Deactivate",
      "action.block": "Block",
      "action.unblock": "Unblock",
      "action.yes": "Yes",
      "action.no": "No",
      "action.sendTest": "Send Test",
      "action.retry": "Retry",
      "action.copy": "Copy",
      "action.loading": "Loading...",
      "action.saving": "Saving...",

      // ── Status labels ──
      "status.pending": "Pending",
      "status.confirmed": "Confirmed",
      "status.processing": "Processing",
      "status.shipped": "Shipped",
      "status.delivered": "Delivered",
      "status.cancelled": "Cancelled",
      "status.returned": "Returned",
      "status.paid": "Paid",
      "status.unpaid": "Unpaid",
      "status.failed": "Failed",
      "status.refunded": "Refunded",
      "status.draft": "Draft",
      "status.published": "Published",
      "status.scheduled": "Scheduled",
      "status.expired": "Expired",
      "status.activeMember": "Active",
      "status.all": "All",
      "status.allStatus": "All Status",
      "status.verified": "Verified",
      "status.unverified": "Unverified",
      "status.inStock": "In Stock",
      "status.outOfStock": "Out of Stock",
      "status.lowStock": "Low Stock",

      // ── Dashboard ──
      "dashboard.title": "Dashboard",
      "dashboard.subtitle": "Overview of your store",
      "dashboard.totalRevenue": "Total Revenue",
      "dashboard.totalOrders": "Total Orders",
      "dashboard.totalCustomers": "Total Customers",
      "dashboard.totalProducts": "Total Products",
      "dashboard.recentOrders": "Recent Orders",
      "dashboard.topProducts": "Top Products",
      "dashboard.activeVip": "Active VIP",
      "dashboard.last30Days": "Last 30 Days",
      "dashboard.today": "Today",
      "dashboard.thisWeek": "This Week",
      "dashboard.thisMonth": "This Month",
      "dashboard.lastMonth": "Last Month",

      // ── Common (tables, forms, filters) ──
      "common.name": "Name",
      "common.email": "Email",
      "common.phone": "Phone",
      "common.address": "Address",
      "common.city": "City",
      "common.state": "State",
      "common.country": "Country",
      "common.pincode": "Pincode",
      "common.price": "Price",
      "common.quantity": "Quantity",
      "common.total": "Total",
      "common.date": "Date",
      "common.dateRange": "Date Range",
      "common.startDate": "Start Date",
      "common.endDate": "End Date",
      "common.createdAt": "Created At",
      "common.updatedAt": "Updated At",
      "common.description": "Description",
      "common.image": "Image",
      "common.images": "Images",
      "common.status": "Status",
      "common.category": "Category",
      "common.brand": "Brand",
      "common.sku": "SKU",
      "common.stock": "Stock",
      "common.actions": "Actions",
      "common.customer": "Customer",
      "common.product": "Product",
      "common.order": "Order",
      "common.orderId": "Order ID",
      "common.method": "Method",
      "common.type": "Type",
      "common.title": "Title",
      "common.content": "Content",
      "common.author": "Author",
      "common.slug": "Slug",
      "common.tags": "Tags",
      "common.role": "Role",
      "common.page": "Page",
      "common.pageOf": "Page {{current}} of {{total}}",
      "common.previous": "Previous",
      "common.next": "Next",
      "common.noResults": "No results found",
      "common.noData": "No data available",
      "common.searchPlaceholder": "Search...",
      "common.selectOption": "Select option",
      "common.required": "Required",
      "common.optional": "Optional",
      "common.showing": "Showing {{from}}-{{to}} of {{total}}",
      "common.perPage": "Per page",

      // ── Page titles ──
      "page.orders": "Orders Management",
      "page.orders.subtitle": "Manage customer orders and track their status",
      "page.customers": "Customers Management",
      "page.customers.subtitle": "Manage customer accounts and information",
      "page.products": "Products",
      "page.products.count": "Products ({{count}})",
      "page.products.subtitle": "Browse, edit and create your catalog",
      "page.blog": "Blog Management",
      "page.blog.subtitle": "Create and manage your blog content",
      "page.analytics": "Analytics",
      "page.analytics.subtitle": "Business insights at a glance",
      "page.inventory": "Inventory",
      "page.inventory.subtitle": "{{total}} products tracked",
      "page.reports": "Reports",
      "page.reports.subtitle": "Export CSV reports for sales, customers and inventory",
      "page.vip": "VIP Wallets",
      "page.vip.subtitle": "Manage farmer memberships and coin balances",
      "page.whatsapp": "WhatsApp Templates",
      "page.whatsapp.subtitle": "Configure BSP requests for each event",
      "page.categories": "Categories",
      "page.brands": "Brands",
      "page.promoCodes": "Promo Codes",
      "page.banners": "Home Banners",
      "page.notifications": "Notifications",
      "page.warehouse": "Warehouse",
      "page.deliveryBoys": "Delivery Boys",
      "page.auditLogs": "Audit Logs",
      "page.settings": "App Settings",
      "page.payments.subtitle": "Transaction ledger and refund tracking",
      "page.payments.pageTotal": "Page total paid",
      "page.payments.paymentId": "Payment ID",

      // ── Stats cards ──
      "stats.totalOrders": "Total Orders",
      "stats.pendingOrders": "Pending Orders",
      "stats.deliveredOrders": "Delivered Orders",
      "stats.totalRevenue": "Total Revenue",
      "stats.totalPosts": "Total Posts",
      "stats.published": "Published",
      "stats.drafts": "Drafts",
      "stats.scheduled": "Scheduled",
      "stats.totalViews": "Total Views",
      "stats.categories": "Categories",
      "stats.totalCustomers": "Total Customers",
      "stats.activeCustomers": "Active Customers",
      "stats.blockedCustomers": "Blocked",
      "stats.vipCustomers": "VIP Members",
      "stats.totalCoins": "Total Coins",
      "stats.expiringSoon": "Expiring Soon",

      // ── Orders ──
      "order.new": "New Order",
      "order.viewOrder": "View Order",
      "order.paymentMethod": "Payment Method",
      "order.paymentStatus": "Payment Status",
      "order.shippingAddress": "Shipping Address",
      "order.billingAddress": "Billing Address",
      "order.items": "Items",
      "order.subtotal": "Subtotal",
      "order.shipping": "Shipping",
      "order.tax": "Tax",
      "order.discount": "Discount",
      "order.grandTotal": "Grand Total",
      "order.markShipped": "Mark as Shipped",
      "order.markDelivered": "Mark as Delivered",
      "order.cancelOrder": "Cancel Order",
      "order.refund": "Refund",

      // ── Products ──
      "product.addProduct": "Add Product",
      "product.editProduct": "Edit Product",
      "product.productName": "Product Name",
      "product.costPrice": "Cost Price",
      "product.sellingPrice": "Selling Price",
      "product.mrp": "MRP",
      "product.stockQuantity": "Stock Quantity",
      "product.lowStockAlert": "Low Stock Alert",
      "product.featured": "Featured",
      "product.mainImage": "Main Image",
      "product.gallery": "Gallery",
      "product.shortDescription": "Short Description",
      "product.longDescription": "Long Description",

      // ── Customers ──
      "customer.addCustomer": "Add Customer",
      "customer.editCustomer": "Edit Customer",
      "customer.farmArea": "Farm Area",
      "customer.soilType": "Soil Type",
      "customer.district": "District",
      "customer.joinedOn": "Joined On",
      "customer.lastOrder": "Last Order",
      "customer.totalSpent": "Total Spent",
      "customer.orderCount": "Orders",

      // ── Blog ──
      "blog.newPost": "Create New Post",
      "blog.editPost": "Edit Post",
      "blog.excerpt": "Excerpt",
      "blog.featuredImage": "Featured Image",
      "blog.publishDate": "Publish Date",
      "blog.seoTitle": "SEO Title",
      "blog.seoDescription": "SEO Description",

      // ── VIP ──
      "vip.grantCoins": "Grant Coins",
      "vip.adjustBalance": "Adjust Balance",
      "vip.renewMembership": "Renew Membership",
      "vip.expireMembership": "Expire Membership",
      "vip.coinsBalance": "Coins Balance",
      "vip.membershipStatus": "Membership Status",
      "vip.expiresAt": "Expires At",
      "vip.transactionHistory": "Transaction History",
      "vip.renewedUntil": "Renewed until {{date}}",

      // ── Reports ──
      "reports.sales": "Sales Report",
      "reports.customers": "Customers Report",
      "reports.inventory": "Inventory Report",
      "reports.downloadCsv": "Download CSV",
      "reports.thisMonth": "This Month",
      "reports.last30Days": "Last 30 Days",

      // ── WhatsApp ──
      "whatsapp.event": "Event",
      "whatsapp.template": "Template",
      "whatsapp.dispatches": "Dispatches",
      "whatsapp.testNumber": "Test Number",
      "whatsapp.activate": "Activate",
      "whatsapp.deactivate": "Deactivate",

      // ── Auth ──
      "auth.login": "Login",
      "auth.logout": "Logout",
      "auth.email": "Email",
      "auth.password": "Password",
      "auth.forgotPassword": "Forgot Password?",
      "auth.welcomeBack": "Welcome Back",
      "auth.signInToContinue": "Sign in to continue",

      // ── Language toggle ──
      "language.english": "English",
      "language.hindi": "हिंदी",
      "language.switchTo": "Switch language",

      // ── Toasts ──
      "toast.saved": "Saved successfully",
      "toast.deleted": "Deleted successfully",
      "toast.updated": "Updated successfully",
      "toast.error": "Something went wrong",
      "toast.networkError": "Network error — please try again",
    },
  },
  hi: {
    translation: {
      "sidebar.dashboard": "डैशबोर्ड",
      "sidebar.products": "उत्पाद",
      "sidebar.orders": "ऑर्डर",
      "sidebar.customers": "ग्राहक",
      "sidebar.categories": "श्रेणियाँ",
      "sidebar.brands": "ब्रांड",
      "sidebar.flashSales": "फ़्लैश सेल",
      "sidebar.promoCodes": "प्रोमो कोड",
      "sidebar.featuredSections": "फ़ीचर्ड सेक्शन",
      "sidebar.homeBanners": "होम बैनर",
      "sidebar.blog": "ब्लॉग",
      "sidebar.newsletter": "न्यूज़लेटर",
      "sidebar.notifications": "सूचनाएँ",
      "sidebar.supportTickets": "सहायता टिकट",
      "sidebar.returnRequests": "वापसी अनुरोध",
      "sidebar.marketing": "मार्केटिंग",
      "sidebar.warehouse": "गोदाम",
      "sidebar.deliveryBoys": "डिलीवरी बॉय",
      "sidebar.analytics": "विश्लेषण",
      "sidebar.payments": "भुगतान",
      "sidebar.reports": "रिपोर्ट्स",
      "sidebar.vipWallets": "VIP वॉलेट",
      "sidebar.whatsappTemplates": "WhatsApp टेम्पलेट",
      "sidebar.appSettings": "ऐप सेटिंग्स",
      "sidebar.auditLogs": "चेंज लॉग",
      "sidebar.mediaManager": "मीडिया लाइब्रेरी",
      "sidebar.pendingOrders": "पेंडिंग ऑर्डर",
      "sidebar.codCash": "COD कैश",
      "sidebar.customerComms": "ग्राहक संदेश",
      "sidebar.adminUsers": "एडमिन यूज़र",
      "sidebar.settings": "सेटिंग्स",
      "sidebar.content": "कंटेंट",
      "sidebar.support": "सहायता",
      "sidebar.helpAndDocs": "मदद और दस्तावेज़",
      "sidebar.legalPages": "कानूनी पेज",

      "action.save": "सहेजें",
      "action.saveChanges": "परिवर्तन सहेजें",
      "action.cancel": "रद्द करें",
      "action.delete": "हटाएँ",
      "action.edit": "संपादित करें",
      "action.add": "जोड़ें",
      "action.create": "बनाएँ",
      "action.update": "अपडेट करें",
      "action.back": "पीछे",
      "action.download": "डाउनलोड",
      "action.upload": "अपलोड",
      "action.search": "खोजें",
      "action.filter": "फ़िल्टर",
      "action.refresh": "रिफ्रेश",
      "action.apply": "लागू करें",
      "action.confirm": "पुष्टि करें",
      "action.close": "बंद करें",
      "action.reset": "रीसेट",
      "action.submit": "सबमिट",
      "action.export": "एक्सपोर्ट",
      "action.import": "इम्पोर्ट",
      "action.view": "देखें",
      "action.viewAll": "सभी देखें",
      "action.viewDetails": "विवरण देखें",
      "action.active": "सक्रिय",
      "action.inactive": "निष्क्रिय",
      "action.activate": "सक्रिय करें",
      "action.deactivate": "निष्क्रिय करें",
      "action.block": "ब्लॉक करें",
      "action.unblock": "अनब्लॉक",
      "action.yes": "हाँ",
      "action.no": "नहीं",
      "action.sendTest": "टेस्ट भेजें",
      "action.retry": "पुनः प्रयास",
      "action.copy": "कॉपी",
      "action.loading": "लोड हो रहा है...",
      "action.saving": "सहेजा जा रहा है...",

      "status.pending": "लंबित",
      "status.confirmed": "पुष्टि",
      "status.processing": "प्रक्रियाधीन",
      "status.shipped": "भेजा गया",
      "status.delivered": "वितरित",
      "status.cancelled": "रद्द",
      "status.returned": "वापस",
      "status.paid": "भुगतान किया",
      "status.unpaid": "अवैतनिक",
      "status.failed": "विफल",
      "status.refunded": "रिफ़ंड",
      "status.draft": "ड्राफ्ट",
      "status.published": "प्रकाशित",
      "status.scheduled": "निर्धारित",
      "status.expired": "समाप्त",
      "status.activeMember": "सक्रिय",
      "status.all": "सभी",
      "status.allStatus": "सभी स्थिति",
      "status.verified": "सत्यापित",
      "status.unverified": "असत्यापित",
      "status.inStock": "स्टॉक में",
      "status.outOfStock": "स्टॉक में नहीं",
      "status.lowStock": "कम स्टॉक",

      "dashboard.title": "डैशबोर्ड",
      "dashboard.subtitle": "आपके स्टोर का अवलोकन",
      "dashboard.totalRevenue": "कुल राजस्व",
      "dashboard.totalOrders": "कुल ऑर्डर",
      "dashboard.totalCustomers": "कुल ग्राहक",
      "dashboard.totalProducts": "कुल उत्पाद",
      "dashboard.recentOrders": "हाल के ऑर्डर",
      "dashboard.topProducts": "शीर्ष उत्पाद",
      "dashboard.activeVip": "सक्रिय VIP",
      "dashboard.last30Days": "पिछले 30 दिन",
      "dashboard.today": "आज",
      "dashboard.thisWeek": "इस सप्ताह",
      "dashboard.thisMonth": "इस माह",
      "dashboard.lastMonth": "पिछला माह",

      "common.name": "नाम",
      "common.email": "ईमेल",
      "common.phone": "फ़ोन",
      "common.address": "पता",
      "common.city": "शहर",
      "common.state": "राज्य",
      "common.country": "देश",
      "common.pincode": "पिन कोड",
      "common.price": "मूल्य",
      "common.quantity": "मात्रा",
      "common.total": "कुल",
      "common.date": "तिथि",
      "common.dateRange": "तिथि सीमा",
      "common.startDate": "प्रारंभ तिथि",
      "common.endDate": "समाप्ति तिथि",
      "common.createdAt": "निर्मित",
      "common.updatedAt": "अद्यतन",
      "common.description": "विवरण",
      "common.image": "छवि",
      "common.images": "छवियाँ",
      "common.status": "स्थिति",
      "common.category": "श्रेणी",
      "common.brand": "ब्रांड",
      "common.sku": "SKU",
      "common.stock": "स्टॉक",
      "common.actions": "क्रियाएँ",
      "common.customer": "ग्राहक",
      "common.product": "उत्पाद",
      "common.order": "ऑर्डर",
      "common.orderId": "ऑर्डर ID",
      "common.method": "विधि",
      "common.type": "प्रकार",
      "common.title": "शीर्षक",
      "common.content": "सामग्री",
      "common.author": "लेखक",
      "common.slug": "स्लग",
      "common.tags": "टैग",
      "common.role": "भूमिका",
      "common.page": "पेज",
      "common.pageOf": "पेज {{current}} / {{total}}",
      "common.previous": "पिछला",
      "common.next": "अगला",
      "common.noResults": "कोई परिणाम नहीं मिला",
      "common.noData": "कोई डेटा उपलब्ध नहीं",
      "common.searchPlaceholder": "खोजें...",
      "common.selectOption": "विकल्प चुनें",
      "common.required": "आवश्यक",
      "common.optional": "वैकल्पिक",
      "common.showing": "{{from}}-{{to}} / कुल {{total}}",
      "common.perPage": "प्रति पेज",

      "page.orders": "ऑर्डर प्रबंधन",
      "page.orders.subtitle": "ग्राहक ऑर्डर प्रबंधित करें और उनकी स्थिति ट्रैक करें",
      "page.customers": "ग्राहक प्रबंधन",
      "page.customers.subtitle": "ग्राहक खातों और जानकारी का प्रबंधन करें",
      "page.products": "उत्पाद",
      "page.products.count": "उत्पाद ({{count}})",
      "page.products.subtitle": "अपनी कैटलॉग ब्राउज़, संपादित और बनाएँ",
      "page.blog": "ब्लॉग प्रबंधन",
      "page.blog.subtitle": "अपनी ब्लॉग सामग्री बनाएँ और प्रबंधित करें",
      "page.analytics": "विश्लेषण",
      "page.analytics.subtitle": "एक नज़र में व्यापार अंतर्दृष्टि",
      "page.inventory": "इन्वेंट्री",
      "page.inventory.subtitle": "{{total}} उत्पाद ट्रैक हो रहे हैं",
      "page.reports": "रिपोर्ट्स",
      "page.reports.subtitle": "बिक्री, ग्राहक और इन्वेंट्री के लिए CSV रिपोर्ट एक्सपोर्ट करें",
      "page.vip": "VIP वॉलेट",
      "page.vip.subtitle": "किसान सदस्यताएँ और सिक्का बैलेंस प्रबंधित करें",
      "page.whatsapp": "WhatsApp टेम्पलेट",
      "page.whatsapp.subtitle": "प्रत्येक इवेंट के लिए BSP अनुरोध कॉन्फ़िगर करें",
      "page.categories": "श्रेणियाँ",
      "page.brands": "ब्रांड",
      "page.promoCodes": "प्रोमो कोड",
      "page.banners": "होम बैनर",
      "page.notifications": "सूचनाएँ",
      "page.warehouse": "गोदाम",
      "page.deliveryBoys": "डिलीवरी बॉय",
      "page.auditLogs": "ऑडिट लॉग",
      "page.settings": "ऐप सेटिंग्स",
      "page.payments.subtitle": "लेनदेन खाता और रिफंड ट्रैकिंग",
      "page.payments.pageTotal": "पेज कुल भुगतान",
      "page.payments.paymentId": "भुगतान ID",

      "stats.totalOrders": "कुल ऑर्डर",
      "stats.pendingOrders": "लंबित ऑर्डर",
      "stats.deliveredOrders": "वितरित ऑर्डर",
      "stats.totalRevenue": "कुल राजस्व",
      "stats.totalPosts": "कुल पोस्ट",
      "stats.published": "प्रकाशित",
      "stats.drafts": "ड्राफ्ट",
      "stats.scheduled": "निर्धारित",
      "stats.totalViews": "कुल दृश्य",
      "stats.categories": "श्रेणियाँ",
      "stats.totalCustomers": "कुल ग्राहक",
      "stats.activeCustomers": "सक्रिय ग्राहक",
      "stats.blockedCustomers": "ब्लॉक किए गए",
      "stats.vipCustomers": "VIP सदस्य",
      "stats.totalCoins": "कुल सिक्के",
      "stats.expiringSoon": "जल्द समाप्त",

      "order.new": "नया ऑर्डर",
      "order.viewOrder": "ऑर्डर देखें",
      "order.paymentMethod": "भुगतान विधि",
      "order.paymentStatus": "भुगतान स्थिति",
      "order.shippingAddress": "शिपिंग पता",
      "order.billingAddress": "बिलिंग पता",
      "order.items": "वस्तुएँ",
      "order.subtotal": "उप-योग",
      "order.shipping": "शिपिंग",
      "order.tax": "कर",
      "order.discount": "छूट",
      "order.grandTotal": "कुल राशि",
      "order.markShipped": "शिप्ड के रूप में चिह्नित करें",
      "order.markDelivered": "वितरित के रूप में चिह्नित करें",
      "order.cancelOrder": "ऑर्डर रद्द करें",
      "order.refund": "रिफ़ंड",

      "product.addProduct": "उत्पाद जोड़ें",
      "product.editProduct": "उत्पाद संपादित करें",
      "product.productName": "उत्पाद नाम",
      "product.costPrice": "लागत मूल्य",
      "product.sellingPrice": "विक्रय मूल्य",
      "product.mrp": "MRP",
      "product.stockQuantity": "स्टॉक मात्रा",
      "product.lowStockAlert": "कम स्टॉक अलर्ट",
      "product.featured": "फ़ीचर्ड",
      "product.mainImage": "मुख्य छवि",
      "product.gallery": "गैलरी",
      "product.shortDescription": "संक्षिप्त विवरण",
      "product.longDescription": "विस्तृत विवरण",

      "customer.addCustomer": "ग्राहक जोड़ें",
      "customer.editCustomer": "ग्राहक संपादित करें",
      "customer.farmArea": "खेत का क्षेत्रफल",
      "customer.soilType": "मिट्टी का प्रकार",
      "customer.district": "ज़िला",
      "customer.joinedOn": "शामिल हुए",
      "customer.lastOrder": "अंतिम ऑर्डर",
      "customer.totalSpent": "कुल खर्च",
      "customer.orderCount": "ऑर्डर",

      "blog.newPost": "नई पोस्ट बनाएँ",
      "blog.editPost": "पोस्ट संपादित करें",
      "blog.excerpt": "अंश",
      "blog.featuredImage": "फ़ीचर्ड छवि",
      "blog.publishDate": "प्रकाशन तिथि",
      "blog.seoTitle": "SEO शीर्षक",
      "blog.seoDescription": "SEO विवरण",

      "vip.grantCoins": "सिक्के प्रदान करें",
      "vip.adjustBalance": "बैलेंस समायोजित करें",
      "vip.renewMembership": "सदस्यता नवीनीकृत करें",
      "vip.expireMembership": "सदस्यता समाप्त करें",
      "vip.coinsBalance": "सिक्का बैलेंस",
      "vip.membershipStatus": "सदस्यता स्थिति",
      "vip.expiresAt": "समाप्ति तिथि",
      "vip.transactionHistory": "लेनदेन इतिहास",
      "vip.renewedUntil": "{{date}} तक नवीनीकृत",

      "reports.sales": "बिक्री रिपोर्ट",
      "reports.customers": "ग्राहक रिपोर्ट",
      "reports.inventory": "इन्वेंट्री रिपोर्ट",
      "reports.downloadCsv": "CSV डाउनलोड",
      "reports.thisMonth": "इस माह",
      "reports.last30Days": "पिछले 30 दिन",

      "whatsapp.event": "इवेंट",
      "whatsapp.template": "टेम्पलेट",
      "whatsapp.dispatches": "डिस्पैच",
      "whatsapp.testNumber": "टेस्ट नंबर",
      "whatsapp.activate": "सक्रिय करें",
      "whatsapp.deactivate": "निष्क्रिय करें",

      "auth.login": "लॉग इन",
      "auth.logout": "लॉग आउट",
      "auth.email": "ईमेल",
      "auth.password": "पासवर्ड",
      "auth.forgotPassword": "पासवर्ड भूल गए?",
      "auth.welcomeBack": "वापसी पर स्वागत है",
      "auth.signInToContinue": "जारी रखने के लिए साइन इन करें",

      "language.english": "English",
      "language.hindi": "हिंदी",
      "language.switchTo": "भाषा बदलें",

      "toast.saved": "सफलतापूर्वक सहेजा गया",
      "toast.deleted": "सफलतापूर्वक हटाया गया",
      "toast.updated": "सफलतापूर्वक अपडेट किया गया",
      "toast.error": "कुछ गलत हो गया",
      "toast.networkError": "नेटवर्क त्रुटि — कृपया पुनः प्रयास करें",
    },
  },
};
